import { useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { api, call } from '../lib/api'
import { meQuery } from '../lib/queries'
import { AuthButton, AuthInput, AuthLayout, FormError } from '../components/auth'

// Landing page for the welcome, team-invite, customer-invite and password-reset emails.
export const Route = createFileRoute('/set-password')({
  validateSearch: (s: Record<string, unknown>): { token?: string } => ({ token: typeof s.token === 'string' ? s.token : undefined }),
  component: SetPassword,
})

function SetPassword() {
  const { token } = Route.useSearch()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function submit(e: React.SubmitEvent<HTMLFormElement>) {
    e.preventDefault()
    const f = new FormData(e.currentTarget)
    const password = String(f.get('password'))
    if (password !== f.get('confirm')) return setError("The passwords don't match")
    setBusy(true)
    setError(null)
    try {
      await call(api.auth['set-password'].$post({ json: { token: token!, password } }))
      qc.clear()
      const me = await qc.fetchQuery(meQuery)
      navigate({ to: !me.business ? '/portal' : me.business.onboarded ? '/' : '/settings', search: !me.business || me.business.onboarded ? undefined : { tab: 'business' } } as never)
    } catch (err) {
      setError((err as Error).message)
      setBusy(false)
    }
  }

  if (!token)
    return (
      <AuthLayout title="Link missing" subtitle="Open the link from your email again.">
        <Link to="/forgot-password" className="font-semibold text-brand hover:underline">Request a new link</Link>
      </AuthLayout>
    )

  return (
    <AuthLayout title="Set your password" subtitle="Choose a password with at least 8 characters.">
      <form onSubmit={submit}>
        <FormError error={error} />
        <AuthInput label="New password" name="password" type="password" autoComplete="new-password" minLength={8} placeholder="at least 8 characters" required autoFocus />
        <AuthInput label="Confirm password" name="confirm" type="password" autoComplete="new-password" minLength={8} placeholder="type it again" required />
        <AuthButton busy={busy}>Save and continue</AuthButton>
      </form>
    </AuthLayout>
  )
}
